import React, { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { Search, Users, Clock, ArrowRight, CheckCircle } from 'lucide-react';

export default function ShiftAssignmentPage() {
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');
  const [shiftFilter, setShiftFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [targetShift, setTargetShift] = useState('');
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const [empRes, shiftRes, deptRes] = await Promise.all([
        api.get('/hr/shift-assignments'),
        api.get('/hr/shifts'),
        api.get('/departments').catch(() => ({ data: [] })),
      ]);
      setEmployees(empRes.data || []);
      setShifts(shiftRes.data || []);
      setDepartments(deptRes.data || []);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }

  const shiftMap = useMemo(() => {
    const m = {};
    shifts.forEach(s => { m[s.id] = s; });
    return m;
  }, [shifts]);

  const filtered = useMemo(() => {
    return employees.filter(emp => {
      const matchSearch = !search || (emp.full_name || '').toLowerCase().includes(search.toLowerCase()) || (emp.employee_id || '').toLowerCase().includes(search.toLowerCase());
      const matchDept = deptFilter === 'all' || emp.department === deptFilter;
      const matchShift = shiftFilter === 'all' || (emp.shift_id || 'unassigned') === shiftFilter;
      return matchSearch && matchDept && matchShift;
    });
  }, [employees, search, deptFilter, shiftFilter]);

  const uniqueDepts = useMemo(() => {
    const set = new Set();
    employees.forEach(e => { if (e.department) set.add(e.department); });
    return [...set].sort();
  }, [employees]);

  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(e => e.id || e.employee_id)));
    }
  }

  async function handleBulkAssign() {
    if (!targetShift) { toast.error('Select a target shift'); return; }
    if (selected.size === 0) { toast.error('Select at least one employee'); return; }
    setAssigning(true);
    try {
      const res = await api.post('/hr/shift-assignments/bulk', {
        shift_id: targetShift,
        employee_ids: [...selected],
      });
      toast.success(res.data?.message || 'Shifts assigned');
      setSelected(new Set());
      setTargetShift('');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to assign shifts');
    } finally {
      setAssigning(false);
    }
  }

  function formatTime(t) {
    if (!t) return '--:--';
    const [h, m] = t.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${h12}:${m} ${ampm}`;
  }

  return (
    <div className="space-y-6" data-testid="shift-assignment-page">
      <div>
        <h1 className="text-2xl font-bold">Employee Shift Assignment</h1>
        <p className="text-muted-foreground text-sm mt-1">Assign shifts to individual employees or teams by department</p>
      </div>

      {/* Bulk Action Bar */}
      {selected.size > 0 && (
        <Card className="border-primary/50 bg-primary/5">
          <CardContent className="py-3 flex items-center gap-4 flex-wrap">
            <Badge variant="default">{selected.size} selected</Badge>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
            <Select value={targetShift} onValueChange={setTargetShift}>
              <SelectTrigger className="w-[240px]" data-testid="target-shift-select">
                <SelectValue placeholder="Select target shift" />
              </SelectTrigger>
              <SelectContent>
                {shifts.map(s => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name} ({formatTime(s.start)} - {formatTime(s.end)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleBulkAssign} disabled={assigning || !targetShift} data-testid="bulk-assign-btn">
              <CheckCircle className="h-4 w-4 mr-2" />
              {assigning ? 'Assigning...' : 'Assign Shift'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>Clear</Button>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-[320px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search by name or ID..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="search-employee" />
        </div>
        <Select value={deptFilter} onValueChange={setDeptFilter}>
          <SelectTrigger className="w-[200px]" data-testid="dept-filter-select">
            <SelectValue placeholder="All Departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {uniqueDepts.map(d => (
              <SelectItem key={d} value={d}>{d}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={shiftFilter} onValueChange={setShiftFilter}>
          <SelectTrigger className="w-[200px]" data-testid="shift-filter-select">
            <SelectValue placeholder="All Shifts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Shifts</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {shifts.map(s => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Badge variant="outline" className="self-center">{filtered.length} employee(s)</Badge>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center">
              <Users className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
              <h3 className="text-lg font-semibold mb-1">No Employees Found</h3>
              <p className="text-muted-foreground text-sm">Adjust your filters or search criteria.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">
                    <Checkbox
                      checked={selected.size === filtered.length && filtered.length > 0}
                      onCheckedChange={toggleAll}
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Designation</TableHead>
                  <TableHead>Current Shift</TableHead>
                  <TableHead>Timing</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((emp, i) => {
                  const empId = emp.id || emp.employee_id;
                  const shift = shiftMap[emp.shift_id];
                  const isSelected = selected.has(empId);
                  return (
                    <TableRow
                      key={empId}
                      className={isSelected ? 'bg-primary/5' : ''}
                      data-testid={`employee-row-${i}`}
                    >
                      <TableCell>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSelect(empId)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{emp.full_name || 'N/A'}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{emp.employee_id || '-'}</TableCell>
                      <TableCell>{emp.department || '-'}</TableCell>
                      <TableCell className="text-sm">{emp.designation || '-'}</TableCell>
                      <TableCell>
                        {shift ? (
                          <Badge variant="outline" className="gap-1">
                            <Clock className="h-3 w-3" />
                            {shift.name}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">Unassigned</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {shift ? `${formatTime(shift.start)} - ${formatTime(shift.end)}` : '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
